import { useContext, useEffect, useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getFlightPriceKey } from 'lib/flights/flightUtils'
import useDefaultAirport from './useDefaultAirport'
import { shallowEqual, useAppDispatch, useAppSelector } from './reduxHooks'
import { fetchFlightPrice } from 'actions/FlightActions'
import { EmptyObject } from 'lib/object/objectUtils'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import type { FlightsClassTypes } from 'constants/flight'
import { EmptyArray } from 'lib/array/arrayUtils'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import useCharterFlightJourneysForHotelDates from './CharterFlights/useCharterFlightJourneysForHotelDates'

interface FlightPriceOptions {
  disabled?: boolean
}

function useFlightPrice(
  destinationAirportCode: string | undefined,
  rooms: Array<App.Occupants> = EmptyArray,
  startDate?: string | moment.Moment,
  endDate?: string | moment.Moment,
  originAirportCode?: string,
  nights?: number,
  options: FlightPriceOptions = EmptyObject,
): [number, boolean] {
  const defaultAirport = useDefaultAirport()
  const origin = originAirportCode ?? defaultAirport?.code
  const dispatch = useAppDispatch()
  const startDateParam = moment.isMoment(startDate) ? startDate.format(ISO_DATE_FORMAT) : startDate
  const endDateParam = moment.isMoment(endDate) ? endDate.format(ISO_DATE_FORMAT) : endDate
  const flightRoom = useMemo(() => countOccupantsForFlights(rooms), [rooms])
  const filterKey = useMemo(() => getFlightPriceKey({
    startDate: startDateParam ?? '',
    endDate: endDateParam ?? '',
    origin: origin ?? '',
    destination: destinationAirportCode ?? '',
    numberOfAdults: flightRoom.adults,
    numberOfChildren: flightRoom.children ?? 0,
    numberOfInfants: flightRoom.infants ?? 0,
    nights: nights ?? 0,
  }), [startDateParam, endDateParam, origin, destinationAirportCode, flightRoom, nights])
  const isHookEnabled = !options.disabled

  useEffect(() => {
    if (destinationAirportCode && startDateParam && endDateParam && isHookEnabled) {
      dispatch(fetchFlightPrice(destinationAirportCode, [flightRoom], startDateParam, endDateParam, origin, nights))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, isHookEnabled])

  const flightPrice = useAppSelector(state => state.flights.flightPrices[filterKey])
  const fetching = useAppSelector(state => state.flights.flightPricesLoading[filterKey])

  return [flightPrice, !!fetching]
}

export default useFlightPrice

export function useOfferPageFlightPrice(): {
  totalFlightPrice: number
  loading: boolean
  feederFlightTotalPrice?: number
  loyaltyEarnKey?: string
} {
  const state = useContext(OfferPageStateContext)

  const {
    offer,
    rooms,
    checkInDate,
    checkOutDate,
    flightOrigin,
    fareClass,
    flightsEnabled,
    charterBundleRoute,
    charterBundleEnabled,
  } = useAppSelector(appState => ({
    offer: state.offer as App.HotelOffer,
    rooms: state.rooms ?? EmptyArray,
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    flightOrigin: state.airport,
    fareClass: state.fareClass,
    flightsEnabled: offerFlightsEnabled(state, appState),
    charterBundleRoute: state.charterBundleRoute,
    charterBundleEnabled: state.charterBundleEnabled,
  }), shallowEqual)

  const nights = checkInDate && checkOutDate ? moment(checkOutDate).diff(checkInDate, 'days') : 1

  const effectiveCharterBundleRoute = charterBundleEnabled ? charterBundleRoute : undefined
  const charterFlight = effectiveCharterBundleRoute ? CHARTER_FLIGHT_DETAILS[effectiveCharterBundleRoute] : undefined

  const needsCharterFeederFlight = flightsEnabled && offer.offerFlightsEnabled && charterFlight && flightOrigin && flightOrigin.code !== charterFlight.originAirport.airportCode

  const shouldUseNormalFlightPrice = flightsEnabled && offer.offerFlightsEnabled && (!charterFlight || needsCharterFeederFlight)

  const shouldUseCharterFlightPrice = flightsEnabled && offer.offerFlightsEnabled && charterFlight

  let flightDestination: string | undefined = undefined
  if (flightsEnabled && offer.offerFlightsEnabled) {
    if (effectiveCharterBundleRoute && needsCharterFeederFlight) {
      flightDestination = charterFlight.originAirport.airportCode
    } else if (!effectiveCharterBundleRoute) {
      flightDestination = offer.flightDestinationPort
    }
  }

  const [flightPrice, flightPriceLoading] = useFlightPrice(
    flightDestination,
    rooms,
    checkInDate,
    checkOutDate,
    flightOrigin?.code,
    nights,
  )

  const [charterFlightPrice, charterFlightPriceLoading, charterLoyaltyEarnKey] = useCharterFlightPrice({
    charterBundleRoute: effectiveCharterBundleRoute,
    checkInDate,
    checkOutDate,
    occupancies: rooms,
    fareClass,
    disabled: !effectiveCharterBundleRoute,
  })

  let totalFlightPrice = 0
  let loading = false

  if (shouldUseCharterFlightPrice) {
    if (charterFlightPrice) {
      totalFlightPrice = charterFlightPrice
    } else {
      // Ensure we don't show flight price if we don't have this yet - otherwise we could end up showing just the feeder flight price as the total
      totalFlightPrice = 0
    }
    loading = loading || charterFlightPriceLoading
  } else if (shouldUseNormalFlightPrice) {
    totalFlightPrice = flightPrice
    loading = flightPriceLoading
  }

  return {
    totalFlightPrice,
    loading,
    feederFlightTotalPrice: needsCharterFeederFlight ? flightPrice : undefined,
    loyaltyEarnKey: charterLoyaltyEarnKey,
  }
}

export function useCharterFlightPrice({
  charterBundleRoute,
  checkInDate,
  checkOutDate,
  occupancies = EmptyArray,
  fareClass,
  disabled,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FlightsClassTypes
  disabled?: boolean
}): [number, boolean, string | undefined] {
  const { departingJourney, returningJourney, fetching, metadata, departingFareFamily, returningFareFamily } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass,
    disabled,
  })

  if (departingFareFamily && returningFareFamily) {
    const departingPrice = departingFareFamily.cost.all ?? 0
    const returningPrice = returningFareFamily.cost.all ?? 0
    return [departingPrice + returningPrice, fetching, departingFareFamily.loyaltyEarnKey]
  }

  if (departingJourney && returningJourney) {
    return [
      departingJourney.price.all.totalFare + returningJourney.price.all.totalFare,
      fetching,
      departingJourney.loyaltyEarnKey,
    ]
  } else if (metadata?.cheapest?.totalPrice) {
    return [metadata?.cheapest?.totalPrice, fetching, undefined]
  } else {
    return [0, fetching, undefined]
  }
}
